import { FC, ReactNode, RefObject, useMemo } from 'react';
import { clsx } from 'clsx';
import Preloader from '../../atoms/Preloader';
import Tooltip from '../../atoms/Tooltip';
import { POPOVER_Z_INDEX } from '../../lib/zIndex';
import type { DropdownPosition } from '../../types/DropdownPosition';
import './DoublePress.scss';

export type DoublePressTone = 'danger' | 'primary';

export interface IDoublePressFeedbackProps {
    armed: boolean;
    /** Кнопка, у которой стоит подсказка. */
    target: RefObject<HTMLElement | null>;
    /** Что скринридер объявит при взводе: вопрос и «Нажмите ещё раз, чтобы …». */
    announcement: ReactNode;
    /** Надпись взведённой кнопки в подсказке — у кнопки-иконки, на которой надпись не видна. */
    title?: ReactNode;
    /** Пояснение у взведённой кнопки — то, чего нет на ней самой. */
    hint?: ReactNode;
    /** `id` пояснения — для `aria-describedby` кнопки. */
    hintId?: string;
    /** Пояснение грузится. */
    busy?: boolean;
    tone?: DoublePressTone;
    /** @default bottom */
    position?: DropdownPosition;
}

/**
 * Отклик взведённой кнопки в два нажатия: живая область для скринридера (всегда в DOM — иначе объявление теряется)
 * и подсказка у кнопки, если есть что показать (надпись кнопки-иконки, пояснение). Подсказка не интерактивна и не
 * забирает фокус: второе нажатие — по самой кнопке.
 */
const DoublePressFeedback: FC<IDoublePressFeedbackProps> = ({
    armed,
    target,
    announcement,
    title,
    hint,
    hintId,
    busy = false,
    tone = 'primary',
    position = 'bottom',
}) => {
    const hasHint = hint !== undefined && hint !== null && hint !== false && hint !== '';
    const hasTip = title !== undefined || hasHint || busy;
    // текст подсказки — одним элементом, пока он не изменился: кнопки в два нажатия стоят в каждой строке таблицы
    const label = useMemo(
        () => (
            <>
                {title !== undefined && (
                    <span className="rf-double-press-hint__title" aria-hidden>
                        {title}
                    </span>
                )}
                {busy ? (
                    <Preloader className="rf-double-press-hint__busy" size={16} />
                ) : (
                    hasHint && (
                        <span id={hintId} className="rf-double-press-hint__text">
                            {hint}
                        </span>
                    )
                )}
            </>
        ),
        [title, busy, hasHint, hintId, hint],
    );

    return (
        <>
            <span className="rf-double-press__status" role="status">
                {armed ? announcement : null}
            </span>
            {hasTip && (
                <Tooltip
                    variant="compact"
                    target={target}
                    opened={armed}
                    position={position}
                    // хвостик — по центру кнопки: у края подсказку сдвигает, и хвостик «со стороны» уезжал от неё
                    arrowPosition="center"
                    zIndex={POPOVER_Z_INDEX}
                    interactive={false}
                    // пояснение кнопка называет сама (aria-describedby на hintId), надпись объявляет живая область
                    describeTrigger={false}
                    className={clsx('rf-double-press-hint', tone === 'danger' && 'rf-double-press-hint--danger')}
                    label={label}
                />
            )}
        </>
    );
};

export default DoublePressFeedback;
